import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Expense } from "@/types/expense";
import { Trash2, User, Users, Receipt, Calendar, IndianRupee, Tag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";

interface ExpenseListProps {
  expenses: Expense[];
  onDelete: (id: string) => void;
  tripParticipants: string[];
}

export const ExpenseList = ({ expenses, onDelete, tripParticipants }: ExpenseListProps) => {
  const { toast } = useToast();
  
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (!expenses || expenses.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center py-12 text-center bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg shadow-sm"
      >
        <Receipt className="h-12 w-12 text-primary mb-4 animate-pulse" />
        <h3 className="text-lg font-semibold mb-2">No Expenses Yet</h3>
        <p className="text-muted-foreground max-w-sm">
          Start tracking your trip expenses by adding your first expense using the form above.
        </p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-4"
      >
        {expenses.map((expense) => (
          <motion.div 
            key={expense.id} 
            variants={item}
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 bg-gradient-to-br from-white to-primary/5">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="space-y-3 flex-1">
                  <div className="flex items-start justify-between">
                    <h3 className="font-medium text-lg">{expense.description}</h3>
                    <span className="text-xl font-bold text-primary flex items-center gap-1">
                      <IndianRupee className="h-4 w-4" />
                      {Number(expense.amount).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {new Date(expense.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="bg-primary/10 flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {expense.category}
                    </Badge>
                    {expense.paidBy && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {expense.paidBy}
                      </Badge>
                    )}
                    {expense.participants && expense.participants.length > 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {expense.participants.length} participants
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{expense.participants.join(", ")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          onDelete(expense.id);
                          toast({
                            title: "Expense Deleted",
                            description: "The expense has been removed from your trip."
                          });
                        }}
                        className="text-destructive hover:text-destructive/90 hover:scale-110 transition-transform"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete expense</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};